import {Hash} from '~/interfaces/hashInterfaces';
import {RegexHelper} from "~/helpers/regexHelper"

export let StringHelper = {
  capitalize(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
  },
  titleize(text: string): string {
    return text
      .split(" ")
      .map(this.capitalize)
      .join(" ");
  },
  lowercase(text: string): string {
    return text?.toLowerCase();
  },

  toUnderscore(text: string): string {
    return this.lowercase(text).replace(/ /g, "_");
  },

  toDash: function(text: string, target: RegExp = /[ ]{1,}/g): string {
    const result = text || "";

    return result
    // replace special character
        .replace(/[!,.*+?^${(|[\\\'\"})\/\]:]/g, '')
        .replace(/&/g, 'and')
      // erase last white space
        .trim()
        // replace target element, which for default is more than 1 whitespace
        .replace(target, "-")
        .toLowerCase()
  },
  camelToDash: function (str: string) {
    return this.toDash(str.replace(/([a-zA-Z])(?=[A-Z])/g, '$1-'));
  },
  camelToSpace: function(str: string) {
    return str.replace(/([a-zA-Z])(?=[A-Z])/g, "$1 ").toLowerCase();
  },
  camelToTitle: function(str: string) {
    return str.replace(/([a-zA-Z])(?=[A-Z])/g, "$1 ");
  },
  camelToUnderscore: function(str: string) {
    return this.toUnderscore(this.camelToSpace(str));
  },
  limitString: function(str: string, limit: number = 38, ellipsisPosition: 'prefix' | 'midpoint' | 'suffix' = 'suffix') {
    let result = str || "";

    if (str.length > limit) {
      if (ellipsisPosition === 'prefix') {
        result = "..." + str.substring(str.length - limit)
      } else if (ellipsisPosition === 'midpoint') {
        limit = Math.floor(limit/2);
        result = str.substring(0, limit) + "..." + str.substring(str.length - limit);
      } else {
        result = str.substring(0, limit - 1) + "...";
      }
    }
    return result;
  },
  dashToCamel: (str: string): string => {
    return StringHelper._toCamel(str, /-/g)
  },
  dashToTitle(text: string) {
    return text
      .replace(/_/g, " ")
      .replace(/(?:^\w|[A-Z]|\b\w)/g, word => {
          return word.toUpperCase();
        })
      .trim()
      .replace(/-/g, " ");
  },
  toCamel: function(str: string): string {
    return StringHelper._toCamel(str, /_/g)
  },
  _toCamel: (str: string, delimiter: RegExp): string => {
    return str
    .replace(delimiter, " ")
    .replace(/(?:^\w|[A-Z]|\b\w)/g, function(word, index) {
      return index === 0 ? word.toLowerCase() : word.toUpperCase();
    })
    .replace(/\s+/g, "");
  },
  toID: function(str: string): string {
    return str
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[\/\-\[\]\\\^]|[$|#{}()*+?.,<>\&\%\!\@\'\";:`_~]/g, " ")
      .replace(/\s+/g, "-")
      .replace(/[^\d\w-]/g, "");
  },
  isPresent(str: string): boolean {
    return str?.length > 0;
  },
  escape(str: string): string {
    return str.replace(/[\-\[\]{}()*+?.,\\\^$|#\s]/g, "\\$&");
  },
  superTrim: function(value) {
    return value.replace(/\s/g, "");
  },
  cleanSpacing: function(value) {
    // erase groups of whitespace
    let sentence = value.replace(/\s\s/g, " ") //replace duplicated for 1
      .replace(/[\t\n\r]/g, " ") //replace the rest of the characters
      .replace(/\s\s/g, ""); //erase duplicated
    return sentence;
  },
  splice(
    str: string,
    index: number,
    deleteCount: number = 0,
    add: string = ""
  ): string {
    // handles negative index
    if (index < 0) {
      index = str.length + index;
      if (index < 0) {
        index = 0;
      }
    }
    return str.slice(0, index) + add + str.slice(index + deleteCount);
  },
  indefinitize(
    str: string,
    consonant: string = "a",
    vowel: string = "an"
  ): string {
    return new RegExp(/^([aeiou])/, "i").test(str)
      ? `${vowel} ${str}`
      : `${consonant} ${str}`;
  },
  interpolate (str: string, params: Hash<string> = {}): string {
    // will match ":name" and "%{name}" formats inside strings and replace them with values from params hash
    return str.replace(new RegExp(/%{([^\s]+)}|:([^\s]+?)(?:\b|$)/g), (match, capturedKey1, capturedKey2) => {
      const key = capturedKey1 || capturedKey2
      return params[key]
    })
  },
  onlyNumbers(text) {
    return text.replace(/[^0-9]/g, "");
  },
  getVueChildrenText(element) {
    // use: StringHelper.getVueChildrenText(this);
    if(element.$children[0]){
      return this.cleanSpacing(element.$children[0].$el.innerText);
    } else {
      return this.cleanSpacing(element.$el.innerText);
    }
  },
  stripHtml(html: string) {
    const htmlRegex = /(<([^>]+)>)/ig;
    const charRegex = /&(nbsp|amp|quot|lt|gt);/g;
    const charDict = {
      "nbsp": " ",
      "amp" : "&",
      "quot": "\"",
      "lt"  : "<",
      "gt"  : ">"
    };
    const strippedText = html.replace(htmlRegex, "");
    return strippedText.replace(charRegex, (match, entity) => charDict[entity]);
  },
  /**
   * Take a string separated by "[...]" and convert to one separated by "."
   * @example
   *   dotToIndex('f_c0388891-16c1-0bed-175a-8723c7b91709[0][f_c422ad34-6c51-6e5f-4718-84ff0d551895][0][f_98790251-6014-6ec6-ef2f-20fc659d741d]')
   *   // => 'f_c0388891-16c1-0bed-175a-8723c7b91709.0.f_c422ad34-6c51-6e5f-4718-84ff0d551895.0.f_98790251-6014-6ec6-ef2f-20fc659d741d'
   */
  indexToDot(indexString: string): string {
    return indexString.replace(/\[(.+?)]/g, ".$1")
  },
  /**
   * split string and return array with first arg the majority of string/path and second arg the last split/last path
   */
  pop(text: string, delimiter: string = '.'): [string, string] {
    const nodes = text.split(delimiter)
    const lastPath = nodes.pop()
    const parentPath = nodes.join(delimiter)
    return [parentPath, lastPath]
  },
  /**
   * Move a char from one index to another in a string
   *
   * @param text the string to manipulate
   * @param char the character to match against
   * @param amount how far to move the +char+ - can be positive or negative
   * @param pad if the +char+ can't be moved that far can pad string with another character until there is enough room
   */
  shiftChar(text: string, char: string, amount: number, pad: string = ''): string {
    let result = text
    const match = text.match(this.escapeRegex(char))
    if (!match) return result

    if (amount < 0) {
      const roomToShift = match.index
      const absAmount = Math.abs(amount)
      if (roomToShift < absAmount) {
        result = pad.repeat(absAmount - roomToShift) + result
      }
      const beforeDecIndex = result.indexOf(char) + amount
      return result.substr(0, beforeDecIndex) +
        char +
        result.substr(beforeDecIndex).replace(char, '')
    } else {
      const roomToShift = text.length - 1 - match.index
      if (roomToShift < amount) {
        result += pad.repeat(amount - roomToShift)
      }
      return result.substr(0, match.index) +
        result.substr(match.index + 1, amount) +
        char +
        result.substr(match.index + amount + 1)
    }
  },
  shiftDecimal(text: string, amount: number): string {
    if (!text?.length) return ''
    if (!text.match(RegexHelper.number)) return ''
    if (text.match(/^\.0*$/) ) return ''

    if (!text.includes('.')) text += '.'
    let sign = ''
    if (text[0] === '+' || text[0] === '-') {
      sign = text[0]
      text = text.substr(1)
    }

    let shifted = this.shiftChar(text, '.', amount, '0')
    // remove trailing '.'
    if (shifted.endsWith('.')) shifted = shifted.substr(0, shifted.length - 1)

    // if only zeroes
    const onlyZeroesMatch = shifted.match(/^0+$/)
    if (onlyZeroesMatch) return '0'

    // remove leading zeroes
    const leadingZeroesMatch = shifted.match(/^(0+)(?:[1-9]|$)/)
    if (leadingZeroesMatch) shifted = shifted.slice(leadingZeroesMatch[1].length)

    // add leading 0 if start is '.'
    if (shifted.startsWith('.')) shifted = '0' + shifted

    // remove trailing zeroes
    const trailingZeroesMatch = shifted.match(/\.0+$/)
    if (trailingZeroesMatch) shifted = shifted.substr(0, trailingZeroesMatch.index)

    return sign + shifted
  },
  escapeRegex(text: string): string {
    return text.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
  },
  isNumber(text: string): boolean {
    return RegexHelper.number.test(text)
  },
  pluralize(word, count = 0, withCount = true) {
    if (count !== 1) {
      if (plurals[word]) {
        word = plurals[word];
      } else {
        word += 's' ;
      }
    }
    return withCount ? `${count} ${word}` : word;
  },
};

const plurals = {
  is: 'are',
  was: 'were',
  property: 'properties',
  party: 'parties',
};
